import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type CancelFn = () => void;

export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
  dependencies: React.DependencyList = [],
): [T, CancelFn] {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<number | null>(null);
  const dependenciesRef = useRef<React.DependencyList>(dependencies);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debounced = useCallback((...args: Parameters<T>) => {
    cancel();
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      callbackRef.current(...args);
    }, delay);
  }, [cancel, delay]) as T;

  useEffect(() => {
    const prev = dependenciesRef.current;
    const hasChanged = prev.length !== dependencies.length
      || prev.some((value, index) => !Object.is(value, dependencies[index]));
    if (hasChanged) {
      dependenciesRef.current = dependencies;
      cancel();
    }
  }, [dependencies, cancel]);

  useEffect(() => cancel, [cancel]);

  return useMemo(() => [debounced, cancel] as [T, CancelFn], [debounced, cancel]);
}

export function useDebouncedValue<T>(value: T, delay: number, dependencies: React.DependencyList = []): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [updateDebounced, cancelUpdate] = useDebouncedCallback(
    (nextValue: T) => setDebouncedValue(nextValue),
    delay,
    dependencies,
  );

  useEffect(() => {
    updateDebounced(value);
    return cancelUpdate;
  }, [value, updateDebounced, cancelUpdate]);

  return debouncedValue;
}

export function useDebounce<T>(value: T, delay: number, dependencies: React.DependencyList = []): T {
  return useDebouncedValue(value, delay, dependencies);
}
